/**
 * Segment: one choice among a few, always visible -- the mode switch,
 * the plane toggles, the project tabs. Single selection by default;
 * `multiple` for a set of independent toggles (planes on or off).
 */
import type { ReactNode } from 'react';
import type { Key } from 'react-aria-components';
import { ToggleButton, ToggleButtonGroup, type ToggleButtonGroupProps } from 'react-aria-components';
import { BRAND, CONTROL, DIM, RADIUS, TEXT, WASH_ALPHA, WEIGHT, tint, usePalette } from '../theme';
import { focusRing } from './action-button';

export type SegmentOption<K extends Key> = { key: K; label: ReactNode; ariaLabel?: string };

export function Segment<K extends Key>({
  options,
  size = 'md',
  ...rest
}: Omit<ToggleButtonGroupProps, 'className' | 'style' | 'children'> & {
  options: readonly SegmentOption<K>[];
  size?: 'sm' | 'md';
}) {
  const p = usePalette();
  return (
    <ToggleButtonGroup
      {...rest}
      style={{
        display: 'inline-flex',
        border: `1px solid ${p.hairline}`,
        borderRadius: RADIUS.md,
        overflow: 'hidden',
        background: p.surfaceGlass,
      }}
    >
      {options.map((o, i) => (
        <ToggleButton
          key={String(o.key)}
          id={o.key}
          aria-label={o.ariaLabel}
          style={(s) => ({
            height: size === 'sm' ? CONTROL.heightSm : CONTROL.height,
            padding: `0 ${size === 'sm' ? CONTROL.padXSm : CONTROL.padX}px`,
            border: 0,
            borderLeft: i > 0 ? `1px solid ${p.hairline}` : 0,
            background: s.isSelected ? tint(BRAND.teal, WASH_ALPHA.selected) : 'transparent',
            color: s.isSelected || s.isHovered ? p.inkStrong : p.inkSecondary,
            font: 'inherit',
            fontSize: size === 'sm' ? TEXT.caption : TEXT.control,
            fontWeight: s.isSelected ? WEIGHT.strong : WEIGHT.regular,
            cursor: s.isDisabled ? 'not-allowed' : 'pointer',
            opacity: s.isDisabled ? DIM.disabled : 1,
            whiteSpace: 'nowrap',
            outline: 'none',
            boxShadow: s.isFocusVisible ? `inset ${focusRing(p)}` : undefined,
          })}
        >
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
